import React, { useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import {
  type CommunityComment,
  useCommunityComments,
  useCommunityEngagement,
  useCommunityPost,
  useCommunityPostSeenList,
  useRecordCommunityPostsSeen,
  useCommunityRealtime,
  useCreateCommunityComment,
  useSetCommunityCommentPinned,
  useSetCommunityPostPinned,
  useToggleCommunityPostLike,
} from '../hooks/useCommunity';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import { SeenByDialog } from '../components/SeenByDialog';
import { hasAdminRole, hasAnyRole } from '../utils/roles';
import PostRichContent from '../components/social/PostRichContent';
import OriginalPostDialog from '../components/social/OriginalPostDialog';
import { canEditWallPost, isPostEdited, readOriginalPost, type OriginalPostSnapshot } from '../utils/postEdit';

const Page = styled.div`
  min-height: 100%;
  background:
    radial-gradient(ellipse 120% 80% at 50% -20%, color-mix(in srgb, var(--primary) 28%, transparent) 0%, transparent 55%),
    radial-gradient(ellipse 60% 40% at 100% 40%, color-mix(in srgb, var(--primary) 8%, transparent) 0%, transparent 50%),
    var(--background);
  padding: clamp(1rem, 2.5vw, 1.75rem);
`;

const Shell = styled.div`
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FrostCard = styled.section`
  border: 1px solid color-mix(in srgb, var(--outline) 85%, transparent);
  border-radius: 22px;
  background: linear-gradient(
    145deg,
    color-mix(in srgb, var(--surface) 94%, transparent) 0%,
    color-mix(in srgb, var(--surface) 88%, transparent) 100%
  );
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow:
    0 1px 0 color-mix(in srgb, var(--onSurface) 6%, transparent),
    0 12px 40px rgba(0, 0, 0, 0.12),
    0 2px 8px rgba(0, 0, 0, 0.06);
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  width: fit-content;
  text-decoration: none;
  color: var(--primary);
  font-size: 0.86rem;
  font-weight: 700;
`;

const PostCard = styled(FrostCard)`
  padding: clamp(1rem, 2vw, 1.2rem);
`;

const Title = styled.h1`
  margin: 0;
  color: var(--onSurface);
  font-family: 'Poppins', system-ui, sans-serif;
  font-size: clamp(1.25rem, 2.4vw, 1.6rem);
  font-weight: 700;
  letter-spacing: -0.02em;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const Meta = styled.p`
  margin: 0.35rem 0 0;
  color: var(--onSurfaceVariant);
  font-size: 0.82rem;
`;

const PinnedTag = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.68rem;
  padding: 0.2rem 0.55rem;
  border: 1px solid color-mix(in srgb, var(--primary) 35%, var(--outline));
  background: color-mix(in srgb, var(--primary) 12%, var(--surface));
  color: var(--primary);
`;

const StatRow = styled.div`
  margin-top: 1rem;
  padding-top: 0.9rem;
  border-top: 1px solid color-mix(in srgb, var(--outline) 70%, transparent);
  display: flex;
  align-items: center;
  gap: 0.7rem;
  flex-wrap: wrap;
`;

const Stat = styled.span`
  font-size: 0.78rem;
  color: var(--onSurfaceVariant);
`;

const ActionButton = styled.button<{ $active?: boolean }>`
  border: 1px solid
    ${({ $active }) =>
      $active
        ? 'color-mix(in srgb, var(--primary) 34%, transparent)'
        : 'color-mix(in srgb, var(--outline) 78%, transparent)'};
  background: ${({ $active }) =>
    $active
      ? 'color-mix(in srgb, var(--primary) 10%, var(--surface))'
      : 'color-mix(in srgb, var(--surface) 84%, transparent)'};
  color: ${({ $active }) => ($active ? 'var(--primary)' : 'var(--onSurfaceVariant)')};
  border-radius: 999px;
  padding: 0.45rem 0.78rem;
  font-size: 0.78rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
`;

const SectionCard = styled(FrostCard)`
  padding: clamp(1rem, 2vw, 1.2rem);
`;

const SectionTitle = styled.h2`
  margin: 0;
  color: var(--onSurface);
  font-family: 'Poppins', system-ui, sans-serif;
  font-size: 1rem;
  font-weight: 700;
`;

const SectionText = styled.p`
  margin: 0.45rem 0 0;
  color: var(--onSurfaceVariant);
  font-size: 0.82rem;
  line-height: 1.5;
`;

const ReplyBanner = styled.div`
  margin-top: 0.8rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 0.8rem;
  border-radius: 14px;
  background: color-mix(in srgb, var(--surfaceVariant, var(--surface)) 62%, transparent);
  border: 1px solid color-mix(in srgb, var(--outline) 75%, transparent);
`;

const ReplyBannerText = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  font-size: 0.78rem;
`;

const GhostButton = styled.button`
  border: 1px solid color-mix(in srgb, var(--outline) 82%, transparent);
  background: color-mix(in srgb, var(--surface) 72%, transparent);
  color: var(--onSurfaceVariant);
  border-radius: 999px;
  padding: 0.38rem 0.72rem;
  font-size: 0.76rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
`;

const CommentInput = styled.textarea`
  resize: vertical;
  min-height: 120px;
  width: 100%;
  box-sizing: border-box;
  margin-top: 0.85rem;
  border-radius: 18px;
  border: 1px solid color-mix(in srgb, var(--outline) 88%, transparent);
  background: color-mix(in srgb, var(--surfaceVariant, var(--surface)) 55%, var(--surface));
  color: var(--onSurface);
  padding: 0.95rem 1rem;
  font-size: 0.92rem;
  line-height: 1.5;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 22%, transparent);
  }
`;

const ActionRow = styled.div`
  margin-top: 0.8rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.65rem;
`;

const PrimaryButton = styled.button`
  border: none;
  background: linear-gradient(
    165deg,
    color-mix(in srgb, var(--primary) 92%, white) 0%,
    var(--primary) 45%,
    var(--primaryDark, var(--primary)) 100%
  );
  color: var(--onPrimary);
  border-radius: 12px;
  padding: 0.55rem 1rem;
  font-size: 0.82rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
`;

const CommentList = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const CommentCard = styled.div<{ $depth: number }>`
  margin-left: ${({ $depth }) => `${Math.min($depth, 3) * 1.2}rem`};
  padding: 0.9rem 1rem;
  border-radius: 18px;
  border: 1px solid color-mix(in srgb, var(--outline) 74%, transparent);
  background: ${({ $depth }) =>
    $depth === 0
      ? 'color-mix(in srgb, var(--surface) 88%, transparent)'
      : 'color-mix(in srgb, var(--surfaceVariant, var(--surface)) 56%, transparent)'};
`;

const CommentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  align-items: center;
`;

const CommentAuthorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-wrap: wrap;
`;

const CommentAuthor = styled.strong`
  color: var(--onSurface);
  font-size: 0.8rem;
`;

const CommentBody = styled.div`
  margin: 0.45rem 0 0;
`;

const EmptyText = styled.p`
  margin: 0.8rem 0 0;
  color: var(--onSurfaceVariant);
  font-size: 0.82rem;
`;

type CommentNode = CommunityComment & { replies: CommentNode[] };

const formatDate = (value?: string | null) => {
  if (!value) return 'Recently';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Recently';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const buildCommentTree = (comments: CommunityComment[]): CommentNode[] => {
  const nodes = new Map<string, CommentNode>();
  comments.forEach((comment) => {
    nodes.set(comment.id, { ...comment, replies: [] });
  });

  const roots: CommentNode[] = [];
  nodes.forEach((node) => {
    const parentId = node.reply_to_id ?? null;
    if (parentId && nodes.has(parentId)) {
      nodes.get(parentId)?.replies.push(node);
    } else {
      roots.push(node);
    }
  });

  return roots;
};

const getCommunityBackHref = (tab: string | null, filter: string | null, page: string | null) => {
  const params = new URLSearchParams();
  if (tab && tab !== 'feed') params.set('tab', tab);
  if (filter && filter !== 'latest') params.set('filter', filter);
  if (page && page !== '1') params.set('page', page);
  const query = params.toString();
  return query ? `/?${query}` : '/';
};

export const CommunityPostPage: React.FC = () => {
  const { postId } = useParams<{ postId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [originalSnapshot, setOriginalSnapshot] = useState<OriginalPostSnapshot | null>(null);
  const { data: profile } = useCurrentUserProfile(user?.id);
  const roles = profile?.roles ?? user?.user_metadata?.roles;
  const canViewSeenStatus = hasAdminRole(roles);
  const canPinWall = hasAnyRole(roles, ['admin', 'super_admin']);
  const [commentValue, setCommentValue] = useState('');
  const [replyTarget, setReplyTarget] = useState<CommunityComment | null>(null);
  const [isSeenDialogOpen, setIsSeenDialogOpen] = useState(false);
  const commentInputRef = useRef<HTMLTextAreaElement | null>(null);
  const backHref = getCommunityBackHref(
    searchParams.get('tab'),
    searchParams.get('filter'),
    searchParams.get('page')
  );

  useCommunityRealtime({ enabled: Boolean(postId), postId });

  const { data: post, isLoading: postLoading, error: postError } = useCommunityPost(postId);
  const { data: comments, isLoading: commentsLoading } = useCommunityComments(postId);
  const { data: engagement } = useCommunityEngagement(user?.id, postId ? [postId] : undefined);
  const { data: seenList, isLoading: seenListLoading } = useCommunityPostSeenList(
    postId,
    canViewSeenStatus && isSeenDialogOpen
  );
  const recordSeen = useRecordCommunityPostsSeen();
  const toggleLike = useToggleCommunityPostLike(user?.id);
  const createComment = useCreateCommunityComment(user, profile?.full_name);
  const setPostPinned = useSetCommunityPostPinned();
  const setCommentPinned = useSetCommunityCommentPinned();

  const commentTree = useMemo(() => buildCommentTree(comments ?? []), [comments]);
  const liked = Boolean(postId && engagement?.likedPostIds.includes(postId));

  React.useEffect(() => {
    if (!post?.id || !user?.id) return;
    recordSeen.mutate([post.id]);
  }, [post?.id, recordSeen, user?.id]);

  const handleReply = (comment: CommunityComment) => {
    setReplyTarget(comment);
    commentInputRef.current?.focus();
  };

  const handleSubmitComment = async () => {
    if (!postId) return;

    await createComment.mutateAsync({
      postId,
      content: commentValue,
      replyToId: replyTarget?.id,
    });
    setCommentValue('');
    setReplyTarget(null);
  };

  const handleTogglePostPinned = async () => {
    if (!post?.id) return;
    await setPostPinned.mutateAsync({ postId: post.id, pinned: !Boolean(post.is_pinned) });
  };

  const handleToggleCommentPinned = async (comment: CommunityComment) => {
    await setCommentPinned.mutateAsync({
      commentId: comment.id,
      pinned: !Boolean(comment.is_pinned),
    });
  };

  const renderThread = (nodes: CommentNode[], depth = 0): React.ReactNode =>
    nodes.map((node) => (
      <React.Fragment key={node.id}>
        <CommentCard $depth={depth}>
          <CommentHeader>
            <CommentAuthorRow>
              <CommentAuthor>{node.author_name || 'Community Member'}</CommentAuthor>
              {node.is_pinned ? <PinnedTag>Pinned</PinnedTag> : null}
            </CommentAuthorRow>
            <Meta>{formatDate(node.created_at)}</Meta>
          </CommentHeader>
          <CommentBody>
            <PostRichContent text={node.content} compact />
          </CommentBody>
          <ActionRow style={{ marginTop: '0.65rem', justifyContent: 'flex-start' }}>
            <GhostButton type="button" onClick={() => handleReply(node)}>
              Reply
            </GhostButton>
            {canPinWall ? (
              <GhostButton
                type="button"
                onClick={() => void handleToggleCommentPinned(node)}
                disabled={setCommentPinned.isPending}
              >
                {node.is_pinned ? 'Unpin' : 'Pin'}
              </GhostButton>
            ) : null}
          </ActionRow>
        </CommentCard>
        {node.replies.length ? renderThread(node.replies, depth + 1) : null}
      </React.Fragment>
    ));

  return (
    <Page>
      <Shell>
        <BackLink to={backHref}>← Back to community</BackLink>

        <PostCard>
          {postLoading ? (
            <SectionText>Loading this conversation...</SectionText>
          ) : postError ? (
            <SectionText>{postError instanceof Error ? postError.message : 'Unable to load this post.'}</SectionText>
          ) : post ? (
            <>
              <TitleRow>
                <div>
                  <Title>{post.title || 'Community conversation'}</Title>
                  <Meta>
                    {post.author_name || 'Community Member'} • {formatDate(post.created_at)}
                    {post.is_pinned ? ' • Pinned' : ''}
                    {isPostEdited(post) ? (
                      <>
                        {' • '}
                        <button
                          type="button"
                          onClick={() => setOriginalSnapshot(readOriginalPost(post))}
                          style={{ background: 'none', border: 0, padding: 0, color: 'inherit', fontWeight: 700, cursor: 'pointer' }}
                        >
                          Edited
                        </button>
                      </>
                    ) : null}
                  </Meta>
                </div>
                {post.is_pinned ? <PinnedTag>Pinned</PinnedTag> : null}
              </TitleRow>
              <div style={{ marginTop: '0.9rem' }}>
                <PostRichContent
                  text={post.content}
                  images={post.images}
                  videoUrl={post.video_url ?? post.video_link ?? undefined}
                  imageAlt={post.title || 'Community post'}
                />
              </div>
              <StatRow>
                <Stat>{post.likesCount ?? post.post_likes?.[0]?.count ?? 0} likes</Stat>
                <Stat>{post.commentsCount ?? post.wall_comments?.[0]?.count ?? 0} comments</Stat>
                {canViewSeenStatus && post.seenCount !== null ? (
                  <ActionButton type="button" onClick={() => setIsSeenDialogOpen(true)}>
                    Seen by {post.seenCount ?? 0}
                  </ActionButton>
                ) : null}
                <ActionButton
                  type="button"
                  $active={liked}
                  onClick={() => postId && toggleLike.mutateAsync(postId)}
                  disabled={toggleLike.isPending}
                >
                  {liked ? 'Liked' : 'Like'}
                </ActionButton>
                {canEditWallPost(post, user?.id) ? (
                  <ActionButton
                    type="button"
                    onClick={() => navigate('/', { state: { editingCommunityPost: post } })}
                  >
                    Edit
                  </ActionButton>
                ) : null}
                {canPinWall ? (
                  <ActionButton
                    type="button"
                    onClick={() => void handleTogglePostPinned()}
                    disabled={setPostPinned.isPending}
                  >
                    {post.is_pinned ? 'Unpin' : 'Pin'}
                  </ActionButton>
                ) : null}
              </StatRow>
            </>
          ) : (
            <SectionText>This post could not be found.</SectionText>
          )}
        </PostCard>

        <SectionCard>
          <SectionTitle>Join the thread</SectionTitle>
          <SectionText>
            Reply to the post directly or pick a comment below to build a nested conversation thread.
          </SectionText>
          {replyTarget ? (
            <ReplyBanner>
              <ReplyBannerText>
                Replying to {replyTarget.author_name || 'Community Member'}: {replyTarget.content.slice(0, 60)}
              </ReplyBannerText>
              <GhostButton type="button" onClick={() => setReplyTarget(null)}>
                Cancel reply
              </GhostButton>
            </ReplyBanner>
          ) : null}
          <CommentInput
            ref={commentInputRef}
            placeholder={replyTarget ? 'Write a nested reply' : 'Write a comment to keep the conversation going'}
            value={commentValue}
            onChange={(event) => setCommentValue(event.target.value)}
          />
          <ActionRow>
            <GhostButton type="button" onClick={() => setReplyTarget(null)}>
              Clear
            </GhostButton>
            <PrimaryButton
              type="button"
              onClick={handleSubmitComment}
              disabled={createComment.isPending || !commentValue.trim()}
            >
              {createComment.isPending ? 'Posting...' : replyTarget ? 'Post Reply' : 'Post Comment'}
            </PrimaryButton>
          </ActionRow>
        </SectionCard>

        <SectionCard>
          <SectionTitle>Replies</SectionTitle>
          <SectionText>Nested replies stay grouped under the conversation they belong to.</SectionText>
          {commentsLoading ? <EmptyText>Loading comments...</EmptyText> : null}
          {!commentsLoading && commentTree.length === 0 ? (
            <EmptyText>Be the first to reply to this post.</EmptyText>
          ) : null}
          <CommentList>{renderThread(commentTree)}</CommentList>
        </SectionCard>

        <OriginalPostDialog snapshot={originalSnapshot} onClose={() => setOriginalSnapshot(null)} />
        <SeenByDialog
          open={isSeenDialogOpen}
          title={post ? `Seen by ${post.seenCount ?? 0}` : 'Seen by'}
          items={seenList ?? []}
          loading={seenListLoading}
          onClose={() => setIsSeenDialogOpen(false)}
        />
      </Shell>
    </Page>
  );
};

export default CommunityPostPage;